import React, { useCallback } from 'react';

import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import { withEventScope } from '@repo/event-processor';
import { Button, greySmallTheme } from '@repo/ui/components/Buttons';
import CopyIcon from '@repo/ui/components/Icon/CopyIcon';
import { fireToast } from '@repo/ui/components/Toast';

import LinkButton from 'components/LinkButton/LinkButton';
import { buildTextUrl } from 'global/urlUtils';

import GeneralLayout from './GeneralLayout';
import ShortLayout from './ShortLayout';
import { GenerationResultCardProps } from './types';

const GenerationResultCard: React.FC<GenerationResultCardProps> = ({
  text,
  isLoading,
  buttonLabel,
  isShort,
}) => {
  const canUserCreateDesign = useDesignsStore(
    designsStoreSelector.canUserCreateDesign
  );

  const copyText = useCallback(async (): Promise<void> => {
    await navigator.clipboard.writeText(text);
    fireToast({ label: 'Quote copied to clipboard' });
  }, [text]);

  const checkIfUserCanCreateDesign = () => {
    const redirectUrl = buildTextUrl(text);
    canUserCreateDesign({
      canShowProjectListModal: true,
      redirectUrl: redirectUrl,
      cb: () => window.open(redirectUrl, '_blank'),
    });
  };

  const renderLinkButton = () => (
    <LinkButton
      isButton
      buttonAction={checkIfUserCanCreateDesign}
      dataTestId="generation-result--button"
      disabled={isLoading}
      height="35px"
      label={buttonLabel}
      style={{
        cursor: isLoading ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
      }}
      theme={{
        ...greySmallTheme,
        padding: '12px 0',
      }}
      width="fit-content"
    />
  );

  const renderCopyButton = () => (
    <Button
      ariaLabel="Copy quote"
      dataTestId="copy-result--button"
      disabled={isLoading}
      height="35px"
      icon={{ component: CopyIcon }}
      name="copyQuoteButton"
      theme={greySmallTheme}
      width="38px"
      onClick={copyText}
    />
  );

  if (isShort) {
    return (
      <ShortLayout
        isLoading={isLoading}
        renderCopyButton={renderCopyButton}
        renderLinkButton={renderLinkButton}
        text={text}
      />
    );
  }

  return (
    <GeneralLayout
      isLoading={isLoading}
      renderCopyButton={renderCopyButton}
      renderLinkButton={renderLinkButton}
      text={text}
    />
  );
};

export default withEventScope(GenerationResultCard, {
  module: 'generationResultCard',
});
